import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ObjectUtils } from 'app/shared/utils/object.utils';
import { TranslationService } from 'app/shared/services/translation.service';
import { TreeItemLike } from '../../../tree-list/tree-list.component';

interface PointHighlightConfigFormLike {
  size?: FormControl<number>;
  fillColor?: FormControl<string>;
}

interface LineHighlightConfigFormLike {
  outlineWidth?: FormControl<number>;
  outlineColor?: FormControl<string>;
}

interface PolygonHighlightConfigFormLike extends LineHighlightConfigFormLike {
  fillColor?: FormControl<string>;
}

interface HighlightConfigGroupFormLike {
  point: FormGroup<PointHighlightConfigFormLike>;
  line: FormGroup<LineHighlightConfigFormLike>;
  polygon: FormGroup<PolygonHighlightConfigFormLike>;
  opacity: FormControl<number>;
}

interface PointHighlightConfigLike {
  size?: number;
  fillColor?: string;
}

interface LineHighlightConfigLike {
  outlineWidth?: number;
  outlineColor?: string;
}

interface PolygonHighlightConfigLike extends LineHighlightConfigLike {
  fillColor?: string;
}

export interface HighlightConfigGroupLike {
  point: PointHighlightConfigLike;
  line: LineHighlightConfigLike;
  polygon: PolygonHighlightConfigLike;
  opacity?: number;
}

@Component({
  selector: 'app-highlight-config-form',
  templateUrl: 'highlight-config-form.component.html',
  styleUrls: ['highlight-config-form.component.scss'],
})
export class HighlightConfigFormComponent implements OnChanges {
  @ViewChild('pointFieldsTpl', { static: false })
  pointFieldsTpl: TemplateRef<any>;
  @ViewChild('lineFieldsTpl', { static: false })
  lineFieldsTpl: TemplateRef<any>;
  @ViewChild('polygonFieldsTpl', { static: false })
  polygonFieldsTpl: TemplateRef<any>;

  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  private _lastValue: HighlightConfigGroupLike;
  @Input()
  value: HighlightConfigGroupLike;
  @Output()
  valueChange = new EventEmitter<HighlightConfigGroupLike>(true);

  formGroup: FormGroup<HighlightConfigGroupFormLike>;

  treeItems: TreeItemLike[];

  constructor(
    private formBuilder: FormBuilder,
    private translationService: TranslationService
  ) {
    this.formGroup = this.formBuilder.group<HighlightConfigGroupFormLike>({
      point: this.formBuilder.group<PointHighlightConfigFormLike>({
        size: this.formBuilder.control<number>(null, [
          Validators.required,
          Validators.min(1),
        ]),
        fillColor: this.formBuilder.control<string>(null, [
          Validators.required,
        ]),
      }),
      line: this.formBuilder.group<LineHighlightConfigFormLike>({
        outlineWidth: this.formBuilder.control<number>(null, [
          Validators.required,
          Validators.min(1),
        ]),
        outlineColor: this.formBuilder.control<string>(null, [
          Validators.required,
        ]),
      }),
      polygon: this.formBuilder.group<PolygonHighlightConfigFormLike>({
        outlineWidth: this.formBuilder.control<number>(null, [
          Validators.required,
          Validators.min(1),
        ]),
        outlineColor: this.formBuilder.control<string>(null, [
          Validators.required,
        ]),
        fillColor: this.formBuilder.control<string>(null, [
          Validators.required,
        ]),
      }),
      opacity: this.formBuilder.control<number>(null, [Validators.required]),
    });
  }

  get symbologyText(): string {
    return this.translationService.getCurrentLanguage()['symbology'];
  }

  get pointsText(): string {
    return this.translationService.getCurrentLanguage()['points'];
  }

  get linesText(): string {
    return this.translationService.getCurrentLanguage()['lines'];
  }

  get polygonsText(): string {
    return this.translationService.getCurrentLanguage()['polygons'];
  }

  get sizeText(): string {
    return this.translationService.getCurrentLanguage()['size'];
  }

  get colorText(): string {
    return this.translationService.getCurrentLanguage()['color'];
  }

  get thicknessText(): string {
    return this.translationService.getCurrentLanguage()['thickness'];
  }

  get transparencyText(): string {
    return this.translationService.getCurrentLanguage()['transparency'];
  }

  get cancelText(): string {
    return this.translationService.getCurrentLanguage()['cancel'];
  }

  get applyText(): string {
    return this.translationService.getCurrentLanguage()['apply'];
  }

  get outlineText(): string {
    return this.translationService.getCurrentLanguage()['outline'];
  }

  get fillText(): string {
    return this.translationService.getCurrentLanguage()['fill'];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.handleValues();
    }
  }

  onContentReady() {
    this.treeItems = [
      {
        text: this.pointsText,
        hasItems: true,
        subListTemplate: this.pointFieldsTpl,
      },
      {
        text: this.linesText,
        hasItems: true,
        subListTemplate: this.lineFieldsTpl,
      },
      {
        text: this.polygonsText,
        hasItems: true,
        subListTemplate: this.polygonFieldsTpl,
      },
    ];
  }

  onHiddenInternal() {
    if (this._lastValue) {
      this.formGroup.reset(this._lastValue);
    }
  }

  onBtnCancelClick() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onSubmit(e) {
    e?.preventDefault();

    if (this.formGroup.invalid) {
      return;
    }

    const values = this.formGroup.getRawValue();
    this._lastValue = null;
    this.value = values;
    this.valueChange.emit(this.value);

    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  private handleValues() {
    this._lastValue = ObjectUtils.clone(this.value);
    this.formGroup.reset(this.value);
  }
}
